import React from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
} from "@mui/material";
import "./CheckModal.css";

function CheckHRModal({
    open,
    onClose,
    analysisData,
    analyzeLoading,
    onCompare,
    compareLoading,
}) {
    const summary = analysisData?.summary;
    const ready = analysisData?.ready_for_compare_hr || [];
    const modified = analysisData?.modified_files || [];
    const unknown = analysisData?.unknown_files || [];
    const restricted = analysisData?.restricted_bin_files || [];
    const excelInfo = analysisData?.excel_info;

    const compareDisabled =
        analyzeLoading ||
        !analysisData ||
        !!analysisData?.excel_info?.error ||
        !analysisData?.files_ready ||
        analysisData.files_ready.length === 0;

    const getIdentifier = (f) => {
        if (f.bins?.length) return f.bins.join("\n");
        if (f.gov_codes?.length) return f.gov_codes.join("\n");
        return "-";
    };

    const renderEmptyRow = (colSpan, text = "Нет данных") => (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" className="precheck-empty">
                {text}
            </TableCell>
        </TableRow>
    );

    return (
        <Modal
            open={open}
            onClose={analyzeLoading ? undefined : onClose}
            disableEscapeKeyDown={analyzeLoading}
        >
            <Box className="precheck-modal-shell">
                <div className="precheck-modal-topbar">
                    <div className="precheck-modal-badge">Предварительная проверка HR</div>
                    <Typography variant="h5" className="precheck-modal-title">
                        Результаты предварительного анализа
                    </Typography>
                </div>

                <Box className="precheck-scroll">
                    {analyzeLoading ? (
                        <Box className="precheck-loading-wrap">
                            <CircularProgress size={58} />
                            <Typography className="precheck-loading-text">
                                Выполняется анализ документов...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {summary && (
                                <section className="precheck-hero-card">
                                    <div className="precheck-hero-head">
                                        <div>
                                            <div className="precheck-kicker">Сводка</div>
                                            <h3 className="precheck-hero-title">
                                                Общая информация
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="precheck-summary-grid">
                                        <div className="precheck-stat-card">
                                            <span className="precheck-stat-label">Банков</span>
                                            <strong>{summary.banks ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card">
                                            <span className="precheck-stat-label">Файлов</span>
                                            <strong>{summary.files ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card">
                                            <span className="precheck-stat-label">Оригинальные</span>
                                            <strong>{summary.original ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-ready">
                                            <span className="precheck-stat-label">К сравнению</span>
                                            <strong>{summary.ready ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-restricted">
                                            <span className="precheck-stat-label">Нет подписки</span>
                                            <strong>{summary.restricted ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-modified">
                                            <span className="precheck-stat-label">Модифицированные</span>
                                            <strong>{summary.modified ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-unknown">
                                            <span className="precheck-stat-label">Неизвестные</span>
                                            <strong>{summary.unknown ?? 0}</strong>
                                        </div>
                                    </div>
                                </section>
                            )}

                            <section className="precheck-section-card">
                                <div className="precheck-section-head">
                                    <div>
                                        <div className="precheck-kicker">Подготовка</div>
                                        <Typography variant="h6" className="precheck-section-title">
                                            Подготовлены к сравнению
                                        </Typography>
                                    </div>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Данные банка
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Банк</TableCell>
                                                <TableCell>Файлов</TableCell>
                                                <TableCell>Платежей</TableCell>
                                                <TableCell>Страниц</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {ready.length > 0 ? (
                                                ready.map((b, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{b.bank_name || "-"}</TableCell>
                                                        <TableCell>{b.files ?? 0}</TableCell>
                                                        <TableCell>{b.payments ?? 0}</TableCell>
                                                        <TableCell>{b.pages ?? 0}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(5)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Данные кадров
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Имя файла</TableCell>
                                                <TableCell>Состояние</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>{excelInfo?.file_name || "-"}</TableCell>
                                                <TableCell
                                                    className={
                                                        excelInfo?.error
                                                            ? "precheck-status-error"
                                                            : "precheck-status-ok"
                                                    }
                                                >
                                                    {excelInfo?.error || "OK"}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </section>

                            <section className="precheck-section-card">
                                <div className="precheck-section-head">
                                    <div>
                                        <div className="precheck-kicker">Исключения</div>
                                        <Typography variant="h6" className="precheck-section-title">
                                            Исключены из сравнения
                                        </Typography>
                                    </div>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Документы не входят в подписку
                                </Typography>
                                <Typography variant="body2" className="precheck-note">
                                    Эти документы распознаны корректно, но не входят в текущую подписку.
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Файл</TableCell>
                                                <TableCell>Платежей</TableCell>
                                                <TableCell>Страниц</TableCell>
                                                <TableCell>БИН / Код ГУ</TableCell>
                                                <TableCell>Банк</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {restricted.length > 0 ? (
                                                restricted.map((f, i) => (
                                                    <TableRow key={i} className="precheck-row-restricted">
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{f.file || "-"}</TableCell>
                                                        <TableCell>{f.payments ?? 0}</TableCell>
                                                        <TableCell>{f.pages ?? 0}</TableCell>
                                                        <TableCell className="precheck-bins">
                                                            {getIdentifier(f)}
                                                        </TableCell>
                                                        <TableCell>{f.bank_name || "-"}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(6)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Модифицированные
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Файл</TableCell>
                                                <TableCell>Платежей</TableCell>
                                                <TableCell>Страниц</TableCell>
                                                <TableCell>Предполагаемый банк</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {modified.length > 0 ? (
                                                modified.map((f, i) => (
                                                    <TableRow key={i} className="precheck-row-modified">
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{f.file || "-"}</TableCell>
                                                        <TableCell>{f.payments ?? 0}</TableCell>
                                                        <TableCell>{f.pages ?? 0}</TableCell>
                                                        <TableCell>{f.bank_name || "-"}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(5)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Неизвестного формата
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Файл</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {unknown.length > 0 ? (
                                                unknown.map((f, i) => (
                                                    <TableRow key={i} className="precheck-row-unknown">
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{f.file || "-"}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(2)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </section>
                        </>
                    )}
                </Box>

                <Box className="precheck-sticky-footer">
                    <Button
                        variant="contained"
                        onClick={onCompare}
                        disabled={compareDisabled || compareLoading}
                        className="precheck-compare-btn"
                    >
                        {compareLoading ? "Сравнение..." : "Сравнить"}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onClose}
                        className="precheck-close-btn"
                        disabled={analyzeLoading}
                    >
                        Закрыть
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

export default CheckHRModal;